export const WORK_STATUS_OPTIONS = [
  ['us_citizen', 'US Citizen'], ['green_card_holder', 'Green card holder'], ['h1b', 'H-1B'],
  ['f1_opt', 'F-1 OPT'], ['f1_stem_opt', 'F-1 STEM OPT'], ['l1', 'L-1'],
  ['tn', 'TN'], ['o1', 'O-1'], ['other', 'Other'],
];

export const APPLICATION_FIELDS = [
  { name: 'authorized_to_work_us', label: 'Are you authorized to work in the US?', type: 'boolean', required: true },
  { name: 'requires_visa_sponsorship', label: 'Do you need visa sponsorship now or in the future?', type: 'boolean', required: true },
  { name: 'us_work_status', label: 'Current US work status', options: WORK_STATUS_OPTIONS, required: true },
  { name: 'at_least_18', label: 'Are you at least 18?', type: 'boolean', required: true },
  { name: 'willing_to_relocate', label: 'Are you willing to relocate?', type: 'boolean', required: true },
  { name: 'start_availability', label: 'When can you start?', maxLength: 255, required: true },
  { name: 'salary_expectations', label: 'Salary expectations', maxLength: 100 },
  { name: 'has_employment_restrictions', label: 'Are you subject to any non-compete or non-solicitation restrictions at your current or most recent employer?', type: 'boolean', required: true },
  { name: 'has_us_federal_or_military_employment', label: 'Are you now, or have you ever been, employed by the U.S. federal government or a U.S. military officer or civilian equivalent?', type: 'boolean', required: true },
];

const PREFER_NOT_TO_SAY = ['prefer_not_to_say', 'Prefer not to say'];
export const DISCLOSURE_FIELDS = [
  { name: 'gender', label: 'Gender', options: [['male', 'Male'], ['female', 'Female'], ['non_binary', 'Non-binary'], PREFER_NOT_TO_SAY] },
  { name: 'race_ethnicity', label: 'Race / Ethnicity', options: [
    ['american_indian_or_alaska_native', 'American Indian or Alaska Native'],
    ['east_asian', 'East Asian (e.g. Chinese, Japanese, Korean)'],
    ['south_asian', 'South Asian (e.g. Indian, Pakistani, Bangladeshi)'],
    ['southeast_asian', 'Southeast Asian (e.g. Filipino, Vietnamese, Thai)'],
    ['black_or_african_american', 'Black or African American'], ['hispanic_or_latino', 'Hispanic or Latino'],
    ['middle_eastern_or_north_african', 'Middle Eastern or North African'],
    ['native_hawaiian_or_pacific_islander', 'Native Hawaiian or Pacific Islander'],
    ['white', 'White'], ['two_or_more_races', 'Two or more races'], PREFER_NOT_TO_SAY,
  ] },
  { name: 'veteran_status', label: 'Veteran status', options: [['yes', 'Yes'], ['no', 'No'], PREFER_NOT_TO_SAY] },
  { name: 'disability_status', label: 'Disability status', options: [['yes', 'Yes'], ['no', 'No'], PREFER_NOT_TO_SAY] },
];

export const PROFILE_LINK_FIELDS = [
  { name: 'linkedin_url', label: 'LinkedIn', placeholder: 'linkedin.com/in/your-name' },
  { name: 'github_url', label: 'GitHub', placeholder: 'github.com/your-name' },
  { name: 'portfolio_url', label: 'Portfolio or website', placeholder: 'yourwebsite.com' },
];

const normalizeAnswer = value => typeof value === 'string' ? value.trim() || null : value ?? null;

// Explicitly select the contract's fields: disclosures must never enter an application payload.
export const getApplicationAnswers = (profile = {}, includeJobSource = false) => {
  const answers = Object.fromEntries(APPLICATION_FIELDS.map(({ name }) => [name, normalizeAnswer(profile[name])]));
  if (includeJobSource) answers.how_did_you_hear_about_job = normalizeAnswer(profile.how_did_you_hear_about_job);
  return answers;
};

export const normalizeApplicationProfile = (profile = {}) => ({
  ...getApplicationAnswers(profile),
  voluntary_disclosures: Object.fromEntries(DISCLOSURE_FIELDS.map(({ name }) => [name, normalizeAnswer(profile.voluntary_disclosures?.[name])])),
});

export const getApplicationProfileChanges = (original, updated) => {
  const before = normalizeApplicationProfile(original);
  const after = normalizeApplicationProfile(updated);
  const changes = {};
  APPLICATION_FIELDS.forEach(({ name }) => { if (before[name] !== after[name]) changes[name] = after[name]; });
  const disclosures = {};
  DISCLOSURE_FIELDS.forEach(({ name }) => {
    if (before.voluntary_disclosures[name] !== after.voluntary_disclosures[name]) disclosures[name] = after.voluntary_disclosures[name];
  });
  if (Object.keys(disclosures).length) changes.voluntary_disclosures = disclosures;
  return changes;
};

export const validateApplicationAnswers = (values, requireAnswers = false) => {
  const errors = {};
  APPLICATION_FIELDS.forEach(field => {
    const value = normalizeAnswer(values[field.name]);
    if (requireAnswers && field.required && value === null) errors[field.name] = 'Please answer this question.';
    else if (value !== null && field.type === 'boolean' && typeof value !== 'boolean') errors[field.name] = 'Select Yes or No.';
    else if (value !== null && field.options && !field.options.some(([code]) => code === value)) errors[field.name] = 'Select a work status.';
    else if (value !== null && field.maxLength && (typeof value !== 'string' || value.length > field.maxLength)) errors[field.name] = `Enter up to ${field.maxLength} characters.`;
  });
  if (values.how_did_you_hear_about_job?.length > 255) errors.how_did_you_hear_about_job = 'Enter up to 255 characters.';
  return errors;
};

export const validateProfileLinks = profile => {
  const errors = {};
  PROFILE_LINK_FIELDS.forEach(({ name }) => {
    const value = (profile[name] || '').trim();
    if (!value) return;
    try {
      const hasScheme = /^[a-z][a-z\d+.-]*:/i.test(value);
      const url = new URL(hasScheme ? value : `https://${value}`);
      if (value.length > 2048 || !['http:', 'https:'].includes(url.protocol) || !url.hostname || /\s/.test(value)) throw new Error();
    } catch {
      errors[name] = 'Enter an http or https URL, up to 2048 characters.';
    }
  });
  return errors;
};

export const getApplicationError = (response, fallback) => {
  const data = response?.response?.data || response || {};
  return {
    message: data.error || data.message || fallback,
    details: Object.fromEntries(Object.entries(data.details || {}).map(([key, value]) => [key.replace(/^(application_profile|application_answers)\./, ''), value])),
  };
};

export const isApiFailure = response => !response || response instanceof Error || response.isAxiosError || Boolean(response.response?.status >= 400) || Boolean(response.status >= 400) || Boolean(response.code?.startsWith('ERR')) || Boolean(response.error);

export const formatSponsorshipAnswer = value => value === true ? 'Required' : value === false ? 'Not Required' : 'Not answered';
